import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { usePageMeta } from '@/hooks/usePageMeta'

const GOLD = '#C9A96E'
const GOLD_SOFT = '#e0c898'
const ACCENT_SOFT = '#d4c5e8'

export default function PageDiamante() {
  usePageMeta({
    title: 'Reacenda o Diamante — Reative os 4 Elementos',
    description: 'Processo de Liberação, Elevação e Cocriação com atendimento individual e intensivo de 126 dias.',
    ogImage: 'https://static.lumi.new/81/81a583c826ca072b58c2a0dc3cc93664.svg',
    ogUrl: 'https://jornada.reacendaluz.com.br/diamante/',
  })

  const navigate = useNavigate()

  return (
    <div className="min-h-screen w-full font-body" style={{ background: '#0a0f14', color: 'var(--ink, #e8e0d5)' }}>

      {/* Header */}
      <header
        className="w-full px-4 py-5 flex items-center justify-center border-b"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p className="text-[11px] tracking-[0.35em] uppercase font-body opacity-60" style={{ color: ACCENT_SOFT }}>
          Reative os <span className="numerals">4</span> Elementos · Mônica Maria
        </p>
      </header>

      <main className="w-full max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* ── HERO ── */}
          <div className="text-center mb-14 md:mb-18">
            {/* Diamond sigil */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.1 }}
              className="flex justify-center mb-7"
            >
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none" aria-hidden="true">
                <polygon
                  points="28,4 52,21 28,52 4,21"
                  fill="none"
                  stroke={GOLD}
                  strokeWidth="1.5"
                  opacity="0.65"
                />
                <polygon points="28,4 52,21 28,52 4,21" fill={`${GOLD}0e`} />
                <polygon
                  points="28,12 44,22 28,44 12,22"
                  fill="none"
                  stroke={GOLD_SOFT}
                  strokeWidth="0.8"
                  opacity="0.35"
                />
                <circle cx="28" cy="28" r="3" fill={GOLD} opacity="0.45" />
              </svg>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-[10px] tracking-[0.4em] uppercase font-body mb-5"
              style={{ color: GOLD_SOFT }}
            >
              Processo Individual · <span className="numerals">126</span> dias
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3 }}
              className="font-display type-hero text-[var(--ink,#e8e0d5)] mb-5"
            >
              Reacenda o{' '}
              <em className="italic font-light" style={{ color: GOLD_SOFT }}>Diamante</em>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.45 }}
              className="font-display italic text-[18px] md:text-[20px] mb-8 max-w-2xl mx-auto leading-relaxed"
              style={{ color: 'rgba(232,224,213,0.7)' }}
            >
              Processo de Liberação, Elevação e Cocriação com atendimento individual e intensivo de <span className="numerals" style={{ color: GOLD_SOFT }}>126</span> dias.
            </motion.p>

            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 80, opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.55 }}
              className="h-px mx-auto"
              style={{ background: `linear-gradient(90deg, transparent, ${GOLD}88, transparent)` }}
            />
          </div>

          {/* ── APRESENTAÇÃO ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="rounded-sm p-6 sm:p-8 md:p-10 mb-12 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(201,169,110,0.06) 0%, rgba(255,255,255,0.02) 100%)',
              border: `1px solid ${GOLD}28`,
            }}
          >
            {/* Corner glow */}
            <div
              className="absolute -top-20 -right-20 w-48 h-48 pointer-events-none"
              style={{ background: `radial-gradient(closest-side, ${GOLD}18, transparent)` }}
            />

            <p className="font-body type-body leading-relaxed mb-8" style={{ color: 'rgba(232,224,213,0.75)' }}>
              Este é um processo complementar ao Protocolo de Liberação e Cocriação que potencializa e aprofunda tudo que foi iniciado nos ciclos, com acompanhamento individual e presença direta minha em cada etapa da sua jornada.
            </p>

            {/* O que está incluso */}
            <div className="mb-8">
              <p
                className="text-[10px] tracking-[0.4em] uppercase font-body mb-5"
                style={{ color: GOLD_SOFT }}
              >
                O que está incluso
              </p>
              <ul className="space-y-3">
                {[
                  { text: '6 ciclos de 21 dias do Protocolo de Liberação e Cocriação' },
                  { text: '3 atendimentos de Cura Quântica Frequencial' },
                  { text: '6 atendimentos individuais de 2 horas, um a cada ciclo, de liberação, expressão e elevação diretamente com Mônica' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className="flex-shrink-0 mt-1"
                      style={{ color: GOLD, fontSize: '14px', lineHeight: 1.6 }}
                    >
                      ✦
                    </span>
                    <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.8)' }}>
                      {item.text}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Divisor */}
            <div
              className="h-px w-full mb-8"
              style={{ background: `linear-gradient(90deg, transparent, ${GOLD}33, transparent)` }}
            />

            {/* Para quem é */}
            <div className="mb-8">
              <p
                className="text-[10px] tracking-[0.4em] uppercase font-body mb-5"
                style={{ color: GOLD_SOFT }}
              >
                Para quem é este processo
              </p>
              <p className="font-body type-body leading-relaxed mb-4" style={{ color: 'rgba(232,224,213,0.75)' }}>
                Para quem sente que carrega camadas profundas que precisam de mais do que um processo solo. Para quem quer transformação real com suporte, presença e direcionamento individual ao longo de{' '}
                <span className="numerals" style={{ color: GOLD_SOFT }}>126</span> dias.
              </p>
              <p
                className="font-display italic text-[16px] md:text-[18px] leading-relaxed"
                style={{ color: `${GOLD_SOFT}cc` }}
              >
                Este é um processo de alto comprometimento e investimento significativo. As vagas são limitadas e as participantes são selecionadas individualmente.
              </p>
            </div>

            {/* ── CTA FORMULÁRIO ── */}
            <div
              className="h-px w-full mb-8"
              style={{ background: `linear-gradient(90deg, transparent, ${GOLD}33, transparent)` }}
            />
            <div className="text-center">
              <button
                onClick={() => navigate('/diamante/formulario')}
                className="inline-block w-full sm:w-auto px-8 py-5 min-h-[60px] rounded-sm font-body type-button tracking-[0.12em] uppercase transition-all duration-300 active:scale-[0.98]"
                style={{
                  background: `linear-gradient(135deg, ${GOLD}22, ${GOLD}14)`,
                  border: `1px solid ${GOLD}66`,
                  color: GOLD_SOFT,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${GOLD}38, ${GOLD}28)`
                  e.currentTarget.style.borderColor = `${GOLD}99`
                  e.currentTarget.style.transform = 'translateY(-1px)'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${GOLD}22, ${GOLD}14)`
                  e.currentTarget.style.borderColor = `${GOLD}66`
                  e.currentTarget.style.transform = 'translateY(0)'
                }}
              >
                Quero preencher o formulário de qualificação
              </button>
            </div>
          </motion.div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer
        className="w-full px-4 py-6 text-center border-t mt-8"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p className="text-[10px] tracking-[0.3em] uppercase font-body opacity-40" style={{ color: ACCENT_SOFT }}>
          Reative os <span className="numerals">4</span> Elementos · Mônica Maria
        </p>
      </footer>
    </div>
  )
}
